'use client';

import { CardCarousel as SharedCardCarousel } from 'components/shared/Card/CardCarousel';

import IconVariant from './variants/IconVariant';
import ImageVariant from './variants/ImageVariant';
import TextVariant from './variants/TextVariant';
import VideoVariant from './variants/VideoVariant';

import { getColorwayName } from 'utils/formatters';
import { cn } from 'utils/styles';

import { liveUpdateAttrs } from 'libs/contentful/utils';

import { CardCarouselProps, CardCarouselClassNames, CardVariant, CardVariantProps } from './types';

const CardCarousel = ({
  sys,
  sectionName,
  color,
  margins,
  cardStyle,
  cards,
  className,
  paginationPortalRef,
}: CardCarouselProps) => {
  const internalClassNames: CardCarouselClassNames = {
    carousel: cn(className?.carousel),
    pagination: cn(className?.pagination),
    cardGrid: cn(className?.cardGrid),
    wrapper: cn(className?.wrapper),
    iconVariant: className?.iconVariant ?? {},
    imageVariant: className?.imageVariant ?? {},
    textVariant: className?.textVariant ?? {},
    videoVariant: className?.videoVariant ?? {},
  };

  const getCardVariant = (props: CardVariantProps) => {
    switch (props.variant) {
      case CardVariant.Icon:
        return (
          <IconVariant
            {...props}
            cardStyle={cardStyle}
            key={props.sys?.id}
            className={internalClassNames.iconVariant}
          />
        );
      case CardVariant.Text:
        return (
          <TextVariant
            {...props}
            cardStyle={cardStyle}
            key={props.sys?.id}
            className={internalClassNames.textVariant}
          />
        );
      case CardVariant.Video:
        return (
          <VideoVariant
            {...props}
            cardStyle={cardStyle}
            key={props.sys?.id}
            className={internalClassNames.videoVariant}
          />
        );
      default:
        return (
          <ImageVariant
            {...props}
            cardStyle={cardStyle}
            key={props.sys?.id}
            className={internalClassNames.imageVariant}
          />
        );
    }
  };

  return (
    <section
      data-testid="section"
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      {...liveUpdateAttrs(sys?.id, 'cardCarousel')}>
      <SharedCardCarousel
        totalItems={cards.items.length}
        color={getColorwayName(color)}
        margins={margins}
        sectionName={sectionName}
        carouselClassName={internalClassNames.carousel}
        paginationClassName={internalClassNames.pagination}
        cardGridClassName={internalClassNames.cardGrid}
        paginationPortalRef={paginationPortalRef}>
        {cards.items.map((card, index) => {
          return getCardVariant({
            ...card,
            index,
            total: cards.items.length,
          });
        })}
      </SharedCardCarousel>
    </section>
  );
};

export default CardCarousel;
